import { Controller } from "@hotwired/stimulus"

// Web shell, pages without a web template (layouts/web). In the iPhone app a
// page's main actions sit in the native nav bar: bridge--button elements (Save,
// Add, Filter) and the overflow menu (bridge--overflow-menu + bridge--menu
// items: Edit, Stop, Delete). Those elements stay hidden in the page, so on web
// this mirrors them as Design C's page actions (.page-head > .page-actions,
// .btn pills, as .web-btn so DaisyUI's .btn stays out of it). Each pill only relays a click to the original element, so forms,
// confirm sheets and Turbo behave exactly as the native tap does.
export default class extends Controller {
  static targets = ["head", "bar"]

  connect() {
    this.observers = []
    const sources = [...this.#buttons(), ...this.#menuItems()]
    if (sources.length === 0) return

    sources.forEach(({ el, primary }) => this.barTarget.append(this.#proxy(el, primary)))
    this.headTarget.hidden = false
  }

  disconnect() {
    this.observers.forEach(observer => observer.disconnect())
    this.barTarget.replaceChildren()
    this.headTarget.hidden = true
  }

  // Native nav-bar buttons the page keeps out of sight.
  #buttons() {
    return [...this.element.querySelectorAll('[data-controller~="bridge--button"]')]
      .filter(el => !this.#visible(el) && !this.#hasVisibleTwin(el))
      .map((el, index) => ({ el, primary: index === 0 }))
  }

  // Items of the nav-bar overflow menu (the one opened from bridge--overflow-menu).
  #menuItems() {
    return [...this.element.querySelectorAll('[data-controller~="bridge--menu"]')]
      .filter(menu => menu.querySelector('[data-controller~="bridge--overflow-menu"]'))
      .flatMap(menu => [...menu.querySelectorAll('[data-bridge--menu-target="item"]')])
      .map(el => ({ el, primary: false }))
  }

  #proxy(el, primary) {
    const button = document.createElement("button")
    button.type = "button"
    const tone = el.dataset.bridgeStyle === "destructive" ? "danger" : (primary ? "primary" : "ghost")
    button.className = `web-btn web-btn-${tone}`
    button.textContent = this.#label(el)
    button.disabled = el.disabled === true
    button.addEventListener("click", () => this.#relay(el))

    const observer = new MutationObserver(() => { button.disabled = el.disabled === true })
    observer.observe(el, { attributes: true, attributeFilter: ["disabled"] })
    this.observers.push(observer)
    return button
  }

  #relay(el) {
    if (el.disabled) return
    if (el.form && el.type === "submit") {
      el.form.requestSubmit(el)
    } else {
      el.click()
    }
  }

  #label(el) {
    return (el.getAttribute("title") || el.dataset.bridgeTitle || el.value || el.textContent || "").trim()
  }

  // The page already shows its own web control for this action (same link or
  // same click handler), as the Transactions filter does.
  #hasVisibleTwin(el) {
    const href = el.getAttribute("href")
    const clicks = (el.dataset.action || "").split(/\s+/).filter(a => a.startsWith("click->"))
    return [...this.element.querySelectorAll("a[href], [data-action]")].some(other =>
      other !== el && this.#visible(other) &&
      ((href && other.getAttribute("href") === href) ||
       clicks.some(a => (other.dataset.action || "").split(/\s+/).includes(a))))
  }

  #visible(el) {
    return el.offsetParent !== null && !el.closest(".hidden, [hidden]")
  }
}
